import { Component } from "react";
import type { ReactNode, ErrorInfo } from "react";
import {
  ErrorContainer,
  ErrorTitle,
  ErrorMessage,
  ErrorDetails,
  ErrorSummary,
  ErrorStack,
  RetryButton,
} from "./index.styled";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class ErrorBoundaryClass extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({
      error,
      errorInfo,
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Log error to console in development
    if (import.meta.env.DEV) {
      console.error("ErrorBoundary caught an error:", error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <ErrorContainer>
          <ErrorTitle>Oops! Something went wrong</ErrorTitle>
          <ErrorMessage>
            We're sorry, but something unexpected happened. Please try
            refreshing the page or contact support if the problem persists.
          </ErrorMessage>

          {import.meta.env.DEV && this.state.error && (
            <ErrorDetails>
              <ErrorSummary>Error Details (Development Mode)</ErrorSummary>
              <div>
                <strong>Error:</strong> {this.state.error.toString()}
              </div>
              {this.state.errorInfo && (
                <div style={{ marginTop: "1rem" }}>
                  <strong>Component Stack:</strong>
                  <ErrorStack>{this.state.errorInfo.componentStack}</ErrorStack>
                </div>
              )}
            </ErrorDetails>
          )}

          <RetryButton onClick={this.handleRetry}>Try Again</RetryButton>
        </ErrorContainer>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundaryClass;
